import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory, useLocation, Switch, Route } from "react-router-dom";
import { toast } from "react-toastify";

// Page specific components
import { PlanCard } from "../components/PageSpecificComponents/pagesComponents/subscriptionPlans";
import {
  ContactModal,
  ChangePlanModal,
  CancelModal
} from "../components/PageSpecificComponents/pagesComponents/billing/PlanModals";
import CheckoutPage from "../components/PageSpecificComponents/pagesComponents/billing/CheckoutPage";
import {
  PageHead,
  sortPlans,
  Banner
} from "../components/PageSpecificComponents/pagesComponents/billing/shared";
import { I } from "../components/PageSpecificComponents/pagesComponents/billing/icons";

// Calls
import {
  fetchSubscriptionPlansRequest,
  resumeSubscribeRequest
} from "../api/calls/subscription.call";

// Actions
import { fetchActiveSubscription } from "../redux/actions/business.actions";

// Utils
import { isEmpty } from "../utils";

const ROADMAP = [
  "Booking widget for your website",
  "Search-engine booking widget",
  "Review replies & management",
  "Integrations marketplace"
];

const RoadmapStrip = () => (
  <div className="roadmap">
    <div>
      <div className="roadmap-title">
        <I.Sparkle width="13" height="13" /> On our roadmap
      </div>
      <div className="roadmap-sub">
        Not included in any plan yet — here's what we're building next.
      </div>
    </div>
    <div className="roadmap-chips">
      {ROADMAP.map(item => (
        <span key={item} className="roadmap-chip">
          {item}
          <span className="roadmap-soon">Coming soon</span>
        </span>
      ))}
    </div>
  </div>
);

const PlansList = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { activeSubscription } = useSelector(state => state.businessProfile);
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [modal, setModal] = useState(null); // {type, plan}
  // Set when the paywall redirect (403 SUBSCRIPTION_REQUIRED) brought us here.
  const isLocked = new URLSearchParams(location.search).get("locked") === "1";

  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      const response = await fetchSubscriptionPlansRequest();
      if (response.hasError) {
        toast.error(response.error.message);
        return;
      }
      setSubscriptionPlans(sortPlans(response.subscriptionPlans));
    };
    fetchSubscriptionPlans();
  }, []);

  useEffect(() => {
    if (isEmpty(activeSubscription)) {
      dispatch(fetchActiveSubscription());
    }
  }, [dispatch, activeSubscription]);

  const activePlanExists = !isEmpty(activeSubscription);
  const isCanceled =
    activePlanExists &&
    (!isEmpty(activeSubscription.canceledAt) ||
      !isEmpty(activeSubscription.cancelAt));

  const resumeSubscription = async () => {
    const response = await resumeSubscribeRequest();
    if (response.hasError) {
      toast.error(response.error.message);
      return;
    }
    toast.success("Subscription resumed.");
    setTimeout(() => dispatch(fetchActiveSubscription()), 1000);
  };

  return (
    <div className="tf-billing">
      <PageHead
        eyebrow="Subscription"
        title="Choose a plan"
        subtitle="Billed monthly in USD. One subscription per business — switch plans anytime, cancel whenever you like."
      />
      {isLocked && (
        <Banner
          variant="warn"
          icon={<I.Lock width="15" height="15" />}
          title="Your subscription has ended"
          text="Pick a plan to regain access to your branches, bookings and team."
        />
      )}
      <div className="plan-grid">
        {subscriptionPlans.map(plan => (
          <PlanCard
            key={plan.id}
            plan={plan}
            isSelected={
              plan.id === activeSubscription?.item?.subscriptionPlan?.id
            }
            activePlanExists={activePlanExists}
            isCanceled={isCanceled}
            onChoose={() =>
              history.push(`/subscription-plans/checkout/${plan.id}`)
            }
            onChangePlan={() => setModal({ type: "change", plan })}
            onCancel={() => setModal({ type: "cancel" })}
            onResume={resumeSubscription}
            onContact={() => setModal({ type: "contact" })}
          />
        ))}
      </div>
      {subscriptionPlans.length > 0 && <RoadmapStrip />}

      {modal?.type === "contact" && (
        <ContactModal onClose={() => setModal(null)} />
      )}
      {modal?.type === "change" && (
        <ChangePlanModal
          fromPlan={activeSubscription?.item?.subscriptionPlan}
          toPlan={modal.plan}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.type === "cancel" && (
        <CancelModal
          activeSubscription={activeSubscription}
          onClose={() => setModal(null)}
        />
      )}
    </div>
  );
};

const SubscriptionPlans = () => (
  <Switch>
    <Route
      path="/subscription-plans/checkout/:planId"
      component={CheckoutPage}
    />
    <Route component={PlansList} />
  </Switch>
);

export default SubscriptionPlans;
